// client/src/pages/admin/ManageBlogs.js

import React, { useEffect, useState } from 'react';
import axios from 'axios';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

export default function ManageBlogs() {
  const token = localStorage.getItem('authToken');
  const [blogs, setBlogs] = useState([]);

  // form fields
  const [blogId, setBlogId] = useState(null);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [seoTitle, setSeoTitle] = useState('');
  const [seoDescription, setSeoDescription] = useState('');

  // New files
  const [images, setImages] = useState([]);
  const [videos, setVideos] = useState([]);

  // Existing media
  const [existingImages, setExistingImages] = useState([]);
  const [existingVideos, setExistingVideos] = useState([]);

  // which to remove
  const [removedImages, setRemovedImages] = useState([]);
  const [removedVideos, setRemovedVideos] = useState([]);

  const fetchBlogs = async () => {
    try {
      const { data } = await axios.get(`${process.env.REACT_APP_API_URL}/api/admin/blog`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBlogs(data);
    } catch (err) {
      console.error('Error fetching blogs:', err);
    }
  };

  useEffect(() => {
    fetchBlogs();
    // eslint-disable-next-line
  }, []);

  const handleSubmit = async () => {
    try {
      const formData = new FormData();
      formData.append('title', title);
      formData.append('content', content);
      formData.append('seoTitle', seoTitle);
      formData.append('seoDescription', seoDescription);

      images.forEach(file => formData.append('images', file));
      videos.forEach(file => formData.append('videos', file));

      // removed
      removedImages.forEach(path => formData.append('removedImages', path));
      removedVideos.forEach(path => formData.append('removedVideos', path));

      if (!blogId) {
        // Create
        await axios.post(`${process.env.REACT_APP_API_URL}/api/admin/blog`, formData, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });
        alert('Blog created!');
      } else {
        // Update
        await axios.put(`${process.env.REACT_APP_API_URL}/api/admin/blog/${blogId}`, formData, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });
        alert('Blog updated!');
      }

      clearForm();
      fetchBlogs();
    } catch (err) {
      console.error(err);
      alert('Failed to submit blog');
    }
  };

  const clearForm = () => {
    setBlogId(null);
    setTitle('');
    setContent('');
    setSeoTitle('');
    setSeoDescription('');
    setImages([]);
    setVideos([]);
    setExistingImages([]);
    setExistingVideos([]);
    setRemovedImages([]);
    setRemovedVideos([]);
  };

  const handleEdit = (blog) => {
    setBlogId(blog._id);
    setTitle(blog.title);
    setContent(blog.content);
    setSeoTitle(blog.seoTitle || '');
    setSeoDescription(blog.seoDescription || '');

    setExistingImages(blog.images || []);
    setExistingVideos(blog.videos || []);
    setImages([]);
    setVideos([]);
    setRemovedImages([]);
    setRemovedVideos([]);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this blog post?')) return;
    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/api/admin/blog/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('Blog deleted');
      fetchBlogs();
    } catch (err) {
      console.error(err);
      alert('Error deleting blog');
    }
  };

  // remove existing images
  const removeExistingImage = (path) => {
    setRemovedImages([...removedImages, path]);
    setExistingImages(existingImages.filter(i => i !== path));
  };

  // remove existing videos
  const removeExistingVideo = (path) => {
    setRemovedVideos([...removedVideos, path]);
    setExistingVideos(existingVideos.filter(v => v !== path));
  };

  return (
    <div>
      <h1 className="text-2xl font-bold mb-4">Manage Blogs</h1>

      {/* CREATE/EDIT FORM */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div>
          <label className="block mb-1 font-medium">Title</label>
          <input
            type="text"
            className="border p-2 w-full"
            value={title}
            onChange={e => setTitle(e.target.value)}
          />
        </div>
        <div>
          <label className="block mb-1 font-medium">SEO Title</label>
          <input
            type="text"
            className="border p-2 w-full"
            value={seoTitle}
            onChange={e => setSeoTitle(e.target.value)}
          />
        </div>
        <div>
          <label className="block mb-1 font-medium">SEO Description</label>
          <input
            type="text"
            className="border p-2 w-full"
            value={seoDescription}
            onChange={e => setSeoDescription(e.target.value)}
          />
        </div>
        <div className="col-span-2">
          <label className="block mb-1 font-medium">Content</label>
          <ReactQuill theme="snow" value={content} onChange={setContent} />
        </div>
        <div>
          <label className="block mb-1 font-medium">Add Images</label>
          <input
            type="file"
            multiple
            accept="image/*"
            onChange={e => setImages(Array.from(e.target.files))}
          />
        </div>
        <div>
          <label className="block mb-1 font-medium">Add Videos</label>
          <input
            type="file"
            multiple
            accept="video/*"
            onChange={e => setVideos(Array.from(e.target.files))}
          />
        </div>
      </div>

      {/* Existing images/videos if editing */}
      {blogId && (
        <div className="mb-6">
          <h3 className="text-lg font-semibold mb-2">Existing Images</h3>
          <div className="flex flex-wrap gap-2">
            {existingImages.map((imgPath, idx) => (
              <div key={idx} className="relative w-24 h-24 border">
                <img
                  src={`${process.env.REACT_APP_API_URL}/${imgPath}`}
                  alt="Blog"
                  className="w-full h-full object-cover"
                />
                <button
                  type="button"
                  onClick={() => removeExistingImage(imgPath)}
                  className="absolute top-0 right-0 bg-red-500 text-white text-xs px-1 py-0.5 rounded"
                >
                  Remove
                </button>
              </div>
            ))}
          </div>

          <h3 className="text-lg font-semibold mt-4 mb-2">Existing Videos</h3>
          <div className="flex flex-wrap gap-2">
            {existingVideos.map((vidPath, idx) => (
              <div key={idx} className="relative w-32 border p-1">
                <video
                  src={`${process.env.REACT_APP_API_URL}/${vidPath}`}
                  controls
                  className="w-full"
                />
                <button
                  type="button"
                  onClick={() => removeExistingVideo(vidPath)}
                  className="absolute top-0 right-0 bg-red-500 text-white text-xs px-1 py-0.5 rounded"
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <button
        onClick={handleSubmit}
        className="bg-primaryGreen text-white px-4 py-2 rounded hover:bg-green-500"
      >
        {blogId ? 'Update Blog' : 'Add Blog'}
      </button>

      <hr className="my-6" />

      {/* LIST BLOGS */}
      <h2 className="text-xl font-bold mb-4">Existing Blogs</h2>
      <div className="space-y-4">
        {blogs.map(blog => (
          <div key={blog._id} className="border p-3 rounded flex justify-between items-start">
            <div>
              <h3 className="font-semibold text-lg">{blog.title}</h3>
              <div
                className="text-gray-700 mt-1"
                dangerouslySetInnerHTML={{ __html: blog.content }}
              />
              <p className="text-sm mt-1">
                <strong>Images:</strong> {blog.images?.length || 0},
                <strong> Videos:</strong> {blog.videos?.length || 0}
              </p>
            </div>
            <div className="space-x-2">
              <button
                onClick={() => handleEdit(blog)}
                className="bg-yellow-500 text-white px-3 py-1 rounded hover:bg-yellow-600"
              >
                Edit
              </button>
              <button
                onClick={() => handleDelete(blog._id)}
                className="bg-red-500 text-white px-3 py-1 rounded hover:bg-red-600"
              >
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
